<template>
  <div class="space-y-8">
    <!-- File ID & End Date -->
    <div>
      <div class="flex items-center gap-2 mb-3">
        <DocumentTextIcon class="h-5 w-5 text-indigo-500" />
        <h4 class="text-base font-semibold tracking-tight text-gray-900">{{ $gettext("Dossier") }}</h4>
      </div>
      <div class="border border-gray-200 rounded-lg p-4">
        <div class="grid grid-cols-2 gap-3">
          <ExtractionInput v-model="data.fileId" :label="$gettext('Numéro de dossier')" />
          <ExtractionInput v-model="data.endDate" type="date" :label="$gettext('Date de fin')" />
        </div>
      </div>
    </div>

    <!-- Landlords -->
    <div>
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <UserGroupIcon class="h-5 w-5 text-indigo-500" />
          <h4 class="text-base font-semibold tracking-tight text-gray-900">{{ $gettext("Bailleurs") }}</h4>
        </div>
        <span class="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
          {{ data.landlords.length }}
        </span>
      </div>

      <div v-for="(landlord, index) in data.landlords" :key="index" class="border border-gray-200 rounded-lg mb-3 overflow-hidden border-l-2 border-l-indigo-200">

        <!-- ===== Company landlord card ===== -->
        <template v-if="landlord.type === 'company'">
          <!-- Collapsible header -->
          <div class="flex items-center bg-gray-50/80">
            <button
              type="button"
              class="flex-1 flex items-center justify-between px-4 py-3 hover:bg-gray-100/80 text-left transition-colors duration-150"
              @click="expandedLandlords[index] = !expandedLandlords[index]"
            >
              <div class="flex items-center gap-3">
                <BriefcaseIcon class="h-4 w-4 text-gray-400 shrink-0" />
                <span class="text-sm font-medium text-gray-700">
                  {{ $gettext("Société") }} {{ index + 1 }}
                  <span v-if="landlord.companyName" class="text-gray-500">— {{ landlord.companyName }}</span>
                </span>
                <span v-if="landlord.matchedCompanyId" class="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                  {{ $gettext("Existante") }}
                </span>
                <span v-else class="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
                  {{ $gettext("Sera créée") }}
                </span>
              </div>
              <ChevronDownIcon class="h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200" :class="expandedLandlords[index] ? 'rotate-180' : ''" />
            </button>
            <button type="button" class="px-3 py-3 text-gray-400 hover:text-red-500 transition-colors" @click.stop="removeLandlord(index)">
              <TrashIcon class="h-4 w-4" />
            </button>
          </div>

          <!-- Card body -->
          <div v-show="expandedLandlords[index]" class="p-4">
            <div class="grid grid-cols-2 gap-3">
              <ExtractionInput v-model="landlord.companyName" :label="$gettext('Dénomination sociale')" required :error="!landlord.matchedCompanyId && !landlord.companyName?.trim()" />
              <ExtractionInput v-model="landlord.companyNumber" :label="$gettext('Numéro d\'entreprise')" />
              <div class="col-span-2">
                <ExtractionAddressAutocomplete v-model="landlord.companyAddress.street" :label="$gettext('Adresse')" :placeholder="$gettext('Rue')" required :error="!landlord.matchedCompanyId && !landlord.companyAddress.street?.trim()" @address-selected="(a) => { landlord.companyAddress.city = a.city; landlord.companyAddress.zipcode = a.zipcode; }" />
                <div class="grid grid-cols-2 gap-2 mt-1">
                  <ExtractionInput v-model="landlord.companyAddress.zipcode" :placeholder="$gettext('Code postal')" required :error="!landlord.matchedCompanyId && !landlord.companyAddress.zipcode?.trim()" />
                  <ExtractionInput v-model="landlord.companyAddress.city" :placeholder="$gettext('Ville')" required :error="!landlord.matchedCompanyId && !landlord.companyAddress.city?.trim()" />
                </div>
              </div>
              <!-- Required fields dropdowns (only for new companies) -->
              <template v-if="!landlord.matchedCompanyId">
                <ExtractionInput v-model="landlord.companyTypeId" type="select" :label="$gettext('Forme juridique')" required :error="!landlord.companyTypeId" :options="companyTypeOptions" />
                <ExtractionInput v-model="landlord.industrySectorId" type="select" :label="$gettext('Secteur d\'activité')" required :error="!landlord.industrySectorId" :options="industrySectorOptions" />
              </template>
            </div>

            <!-- Signatory sub-section -->
            <div v-if="landlord.representative" class="mt-4 pt-4 border-t border-gray-200">
              <h5 class="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">{{ $gettext("Signataire") }}</h5>
              <div class="grid grid-cols-2 gap-3">
                <ExtractionInput v-model="landlord.representative.lastName" :label="$gettext('Nom')" />
                <ExtractionInput v-model="landlord.representative.firstName" :label="$gettext('Prénom')" />
                <ExtractionInput v-model="landlord.representative.role" :label="$gettext('Fonction')" />
                <ExtractionInput v-model="landlord.representative.nationalNumber" :label="$gettext('Numéro national')" />
                <ExtractionInput v-model="landlord.representative.birthdate" type="date" :label="$gettext('Date de naissance')" required :error="!landlord.representative.matchedPhysicalPersonId && (landlord.representative.firstName?.trim() || landlord.representative.lastName?.trim()) && !landlord.representative.birthdate" />
                <ExtractionInput v-model="landlord.representative.birthCity" :label="$gettext('Lieu de naissance')" />
                <ExtractionInput v-model="landlord.representative.nationalityCountryId" type="select" :label="$gettext('Nationalité')" :options="nationalityCountryOptions" required :error="!landlord.representative.matchedPhysicalPersonId && (landlord.representative.firstName?.trim() || landlord.representative.lastName?.trim()) && !landlord.representative.nationalityCountryId" />
                <div class="col-span-2">
                  <ExtractionAddressAutocomplete v-model="landlord.representative.address.street" :label="$gettext('Adresse')" :placeholder="$gettext('Rue')" required :error="!landlord.representative.matchedPhysicalPersonId && !landlord.representative.address.street?.trim()" @address-selected="(a) => { landlord.representative.address.city = a.city; landlord.representative.address.zipcode = a.zipcode; }" />
                  <div class="grid grid-cols-2 gap-2 mt-1">
                    <ExtractionInput v-model="landlord.representative.address.zipcode" :placeholder="$gettext('Code postal')" required :error="!landlord.representative.matchedPhysicalPersonId && !landlord.representative.address.zipcode?.trim()" />
                    <ExtractionInput v-model="landlord.representative.address.city" :placeholder="$gettext('Ville')" required :error="!landlord.representative.matchedPhysicalPersonId && !landlord.representative.address.city?.trim()" />
                  </div>
                </div>
                <!-- PEP questions for representative (only for new physical persons) -->
                <template v-if="!landlord.representative.matchedPhysicalPersonId">
                  <ExtractionInput v-model="landlord.representative.isPoliticalyExposed" type="boolean" :label="$gettext('Politiquement exposée')" required :error="landlord.representative.isPoliticalyExposed === null" />
                  <ExtractionInput v-model="landlord.representative.isCloseToPoliticalyExposed" type="boolean" :label="$gettext('Proche d\'un personne politiquement exposée')" required :error="landlord.representative.isCloseToPoliticalyExposed === null" />
                  <ExtractionInput v-model="landlord.representative.identificationType" type="select" :label="$gettext('Moyen d\'identification')" required :error="!landlord.representative.identificationType" :options="identificationTypeOptions" />
                  <ExtractionInput v-model="landlord.representative.professionId" type="select" :label="$gettext('Profession')" required :error="!landlord.representative.professionId" :options="professionOptions" />
                  <ExtractionInput v-model="landlord.representative.industrySectorId" type="select" :label="$gettext('Secteur d\'activité')" required :error="!landlord.representative.industrySectorId" :options="industrySectorOptions" />
                </template>
                <!-- ID document upload for representative -->
                <IdentityDocumentUpload
                  v-if="showIdentityExtraction"
                  :person="landlord"
                  :person-index="index"
                  :is-extracting="extractingIdentityIndex === index"
                  :identity-error="identityErrorIndex === index ? identityError : null"
                  :label="$gettext('Documents d\'identité du signataire')"
                  @add-file="({ event }) => onIdentityFileAdd(event, index)"
                  @remove-file="({ fileIndex }) => removeIdentityFile(index, fileIndex)"
                  @extract="() => onExtractIdentity(index)"
                />
              </div>
            </div>
          </div>
        </template>

        <!-- ===== Physical person landlord card ===== -->
        <template v-else>
          <!-- Collapsible header -->
          <div class="flex items-center bg-gray-50/80">
            <button
              type="button"
              class="flex-1 flex items-center justify-between px-4 py-3 hover:bg-gray-100/80 text-left transition-colors duration-150"
              @click="expandedLandlords[index] = !expandedLandlords[index]"
            >
              <div class="flex items-center gap-3">
                <UserIcon class="h-4 w-4 text-gray-400 shrink-0" />
                <span class="text-sm font-medium text-gray-700">
                  {{ $gettext("Bailleur") }} {{ index + 1 }}
                  <span v-if="landlord.firstName || landlord.lastName" class="text-gray-500">— {{ [landlord.firstName, landlord.lastName].filter(Boolean).join(' ') }}</span>
                </span>
                <span v-if="landlord.matchConfidence === 'high'" class="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                  {{ $gettext("Existant") }}
                </span>
                <span v-else-if="landlord.matchConfidence === 'medium'" class="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                  {{ $gettext("Correspondance partielle") }}
                </span>
                <span v-else class="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
                  {{ $gettext("Sera créé") }}
                </span>
              </div>
              <ChevronDownIcon class="h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200" :class="expandedLandlords[index] ? 'rotate-180' : ''" />
            </button>
            <button type="button" class="px-3 py-3 text-gray-400 hover:text-red-500 transition-colors" @click.stop="removeLandlord(index)">
              <TrashIcon class="h-4 w-4" />
            </button>
          </div>

          <!-- Card body -->
          <div v-show="expandedLandlords[index]" class="p-4">
            <div class="grid grid-cols-2 gap-3">
              <ExtractionInput v-model="landlord.lastName" :label="$gettext('Nom')" />
              <ExtractionInput v-model="landlord.firstName" :label="$gettext('Prénom')" />
              <ExtractionInput v-model="landlord.birthdate" type="date" :label="$gettext('Date de naissance')" required :error="!landlord.matchedPhysicalPersonId && (landlord.firstName?.trim() || landlord.lastName?.trim()) && !landlord.birthdate" />
              <ExtractionInput v-model="landlord.birthCity" :label="$gettext('Lieu de naissance')" />
              <ExtractionInput v-model="landlord.nationalNumber" :label="$gettext('Numéro national')" />
              <ExtractionInput v-model="landlord.nationalityCountryId" type="select" :label="$gettext('Nationalité')" :options="nationalityCountryOptions" required :error="!landlord.matchedPhysicalPersonId && (landlord.firstName?.trim() || landlord.lastName?.trim()) && !landlord.nationalityCountryId" />
              <div class="col-span-2">
                <ExtractionAddressAutocomplete v-model="landlord.address.street" :label="$gettext('Adresse')" :placeholder="$gettext('Rue')" required :error="!landlord.matchedPhysicalPersonId && !landlord.address.street?.trim()" @address-selected="(a) => { landlord.address.city = a.city; landlord.address.zipcode = a.zipcode; }" />
                <div class="grid grid-cols-2 gap-2 mt-1">
                  <ExtractionInput v-model="landlord.address.zipcode" :placeholder="$gettext('Code postal')" required :error="!landlord.matchedPhysicalPersonId && !landlord.address.zipcode?.trim()" />
                  <ExtractionInput v-model="landlord.address.city" :placeholder="$gettext('Ville')" required :error="!landlord.matchedPhysicalPersonId && !landlord.address.city?.trim()" />
                </div>
              </div>
              <!-- Required fields (only for new physical persons) -->
              <template v-if="!landlord.matchedPhysicalPersonId">
                <ExtractionInput v-model="landlord.isPoliticalyExposed" type="boolean" :label="$gettext('Politiquement exposée')" required :error="landlord.isPoliticalyExposed === null" />
                <ExtractionInput v-model="landlord.isCloseToPoliticalyExposed" type="boolean" :label="$gettext('Proche d\'un personne politiquement exposée')" required :error="landlord.isCloseToPoliticalyExposed === null" />
                <ExtractionInput v-model="landlord.identificationType" type="select" :label="$gettext('Moyen d\'identification')" required :error="!landlord.identificationType" :options="identificationTypeOptions" />
                <ExtractionInput v-model="landlord.professionId" type="select" :label="$gettext('Profession')" required :error="!landlord.professionId" :options="professionOptions" />
                <ExtractionInput v-model="landlord.industrySectorId" type="select" :label="$gettext('Secteur d\'activité')" required :error="!landlord.industrySectorId" :options="industrySectorOptions" />
              </template>
              <!-- ID document upload -->
              <IdentityDocumentUpload
                v-if="showIdentityExtraction"
                :person="landlord"
                :person-index="index"
                :is-extracting="extractingIdentityIndex === index"
                :identity-error="identityErrorIndex === index ? identityError : null"
                @add-file="({ event }) => onIdentityFileAdd(event, index)"
                @remove-file="({ fileIndex }) => removeIdentityFile(index, fileIndex)"
                @extract="() => onExtractIdentity(index)"
              />
            </div>
          </div>
        </template>

      </div>
      <p v-if="!isValid" class="mt-2 text-xs text-red-600">
        {{ $gettext("Veuillez remplir tous les champs obligatoires (*) avant de confirmer.") }}
      </p>
    </div>

    <!-- Properties -->
    <div>
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <HomeIcon class="h-5 w-5 text-indigo-500" />
          <h4 class="text-base font-semibold tracking-tight text-gray-900">{{ $gettext("Propriétés") }}</h4>
        </div>
        <span class="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
          {{ data.properties.length }}
        </span>
      </div>

      <div v-for="(property, index) in data.properties" :key="index" class="border border-gray-200 rounded-lg mb-3 overflow-hidden border-l-2 border-l-indigo-200">
        <!-- Collapsible header -->
        <button
          type="button"
          class="w-full flex items-center justify-between px-4 py-3 bg-gray-50/80 hover:bg-gray-100/80 text-left transition-colors duration-150"
          @click="expandedProperties[index] = !expandedProperties[index]"
        >
          <div class="flex items-center gap-3">
            <HomeModernIcon class="h-4 w-4 text-gray-400 shrink-0" />
            <span class="text-sm font-medium text-gray-700">
              {{ $gettext("Propriété") }} {{ index + 1 }}
              <span v-if="property.address?.street" class="text-gray-500">— {{ property.address.street }}, {{ property.address.city }}</span>
            </span>
            <span v-if="property.matchedPropertyId" class="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
              {{ $gettext("Existante") }}
            </span>
            <span v-else class="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20">
              {{ $gettext("Sera créée") }}
            </span>
          </div>
          <ChevronDownIcon class="h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200" :class="expandedProperties[index] ? 'rotate-180' : ''" />
        </button>

        <!-- Card body -->
        <div v-show="expandedProperties[index]" class="p-4">
          <div class="grid grid-cols-2 gap-3">
            <div class="col-span-2">
              <ExtractionAddressAutocomplete v-model="property.address.street" :label="$gettext('Adresse')" :placeholder="$gettext('Rue')" required :error="!property.matchedPropertyId && !property.address.street?.trim()" @address-selected="(a) => { property.address.city = a.city; property.address.zipcode = a.zipcode; }" />
              <div class="grid grid-cols-3 gap-2 mt-1">
                <ExtractionInput v-model="property.address.apartmentNumber" :placeholder="$gettext('Appart.')" />
                <ExtractionInput v-model="property.address.zipcode" :placeholder="$gettext('Code postal')" required :error="!property.matchedPropertyId && !property.address.zipcode?.trim()" />
                <ExtractionInput v-model="property.address.city" :placeholder="$gettext('Ville')" required :error="!property.matchedPropertyId && !property.address.city?.trim()" />
              </div>
            </div>
            <ExtractionInput v-model="property.listingRent" type="number" :label="$gettext('Loyer de mise en location')" />
            <ExtractionInput v-model="property.surfaceArea" type="number" :label="$gettext('Superficie (m²)')" :min="0" :error="!!property.surfaceArea && property.surfaceArea < 0" />
            <ExtractionInput v-model="property.details" :label="$gettext('Détails')" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, watch, toRaw } from "vue";
import { DocumentTextIcon, UserGroupIcon, HomeIcon, HomeModernIcon, BriefcaseIcon, UserIcon, ChevronDownIcon, TrashIcon } from "@heroicons/vue/24/outline";
import { useIndustrySectorStore } from "@/stores/industrySector";
import { useProfessionStore } from "@/stores/profession";
import { useCompanyTypeStore } from "@/stores/companyType";
import { useCountryStore } from "@/stores/country";
import IdentityDocumentUpload from "@/components/ui/IdentityDocumentUpload.vue";
import ExtractionInput from "@/components/ui/ExtractionInput.vue";
import ExtractionAddressAutocomplete from "@/components/ui/ExtractionAddressAutocomplete.vue";

const props = defineProps({
  extractionData: { type: Object, required: true },
  showIdentityExtraction: { type: Boolean, default: true }
});

const emit = defineEmits(["extract-identity"]);

const industrySectorStore = useIndustrySectorStore();
const professionStore = useProfessionStore();
const companyTypeStore = useCompanyTypeStore();
const countryStore = useCountryStore();

// Fetch Belgian company types when countries are loaded
watch(() => countryStore.sortedCountries, (countries) => {
  if (!countries?.length) return;
  const belgium = countries.find(c => c.attributes.code === "BE");
  if (belgium) companyTypeStore.fetchCompanyTypes(belgium.id);
}, { immediate: true });

const industrySectorOptions = computed(() =>
  industrySectorStore.industrySectors.map(is => ({ value: is.id, label: is.attributes.name }))
);
const professionOptions = computed(() =>
  professionStore.professions.map(p => ({ value: p.id, label: p.attributes.name }))
);
const companyTypeOptions = computed(() =>
  companyTypeStore.companyTypes.map(ct => ({ value: ct.id, label: ct.attributes.name }))
);
const nationalityCountryOptions = computed(() =>
  countryStore.sortedCountries.map(c => ({ value: c.id, label: c.attributes.demonym || c.attributes.name }))
);
const identificationTypeOptions = [
  { value: "faceToFace", label: "Face à face" },
  { value: "remote", label: "A distance" },
  { value: "none", label: "Aucun" }
];

const extractingIdentityIndex = ref(null);
const identityError = ref(null);
const identityErrorIndex = ref(null);

const data = reactive({
  fileId: props.extractionData.fileId || "",
  endDate: props.extractionData.endDate || "",
  landlords: (props.extractionData.landlords || []).map(l => ({
    ...l,
    type: l.type || "physicalPerson",
    identityFiles: [],
    identityExtracted: false,
    companyName: l.companyName || "",
    companyNumber: l.companyNumber || "",
    companyTypeId: l.companyTypeId || null,
    industrySectorId: l.industrySectorId || null,
    companyAddress: {
      street: l.companyAddress?.street || "",
      city: l.companyAddress?.city || "",
      zipcode: l.companyAddress?.zipcode || ""
    },
    representative: l.representative ? {
      ...l.representative,
      role: l.representative.role || "",
      birthCity: l.representative.birthCity || "",
      isPoliticalyExposed: l.representative.isPoliticalyExposed ?? null,
      isCloseToPoliticalyExposed: l.representative.isCloseToPoliticalyExposed ?? null,
      identificationType: l.representative.identificationType || null,
      professionId: l.representative.professionId || null,
      nationalityCountryId: l.representative.nationalityCountryId || null,
      industrySectorId: l.representative.industrySectorId || null,
      address: {
        street: l.representative.address?.street || "",
        city: l.representative.address?.city || "",
        zipcode: l.representative.address?.zipcode || ""
      }
    } : null,
    isPoliticalyExposed: l.isPoliticalyExposed ?? null,
    isCloseToPoliticalyExposed: l.isCloseToPoliticalyExposed ?? null,
    identificationType: l.identificationType || null,
    professionId: l.professionId || null,
    nationalityCountryId: l.nationalityCountryId || null,
    birthCity: l.birthCity || "",
    address: { street: l.address?.street || "", city: l.address?.city || "", zipcode: l.address?.zipcode || "" }
  })),
  properties: (props.extractionData.properties || []).map(p => ({
    ...p,
    surfaceArea: p.surfaceArea || null,
    address: {
      street: p.address?.street || "",
      city: p.address?.city || "",
      zipcode: p.address?.zipcode || "",
      apartmentNumber: p.address?.apartmentNumber || ""
    }
  }))
});

// Collapsible state — all expanded by default
const expandedLandlords = ref(data.landlords.map(() => true));
const expandedProperties = ref(data.properties.map(() => true));

const removeLandlord = (index) => {
  data.landlords.splice(index, 1);
  expandedLandlords.value.splice(index, 1);
};

const isValid = computed(() => {
  const landlordsValid = data.landlords.every(landlord => {
    if (landlord.type === "company") {
      if (!landlord.matchedCompanyId) {
        if (!landlord.companyName?.trim()) return false;
        if (!landlord.companyAddress.street?.trim()) return false;
        if (!landlord.companyAddress.zipcode?.trim()) return false;
        if (!landlord.companyAddress.city?.trim()) return false;
        if (!landlord.companyTypeId) return false;
        if (!landlord.industrySectorId) return false;
      }
      if (landlord.representative && !landlord.representative.matchedPhysicalPersonId) {
        if (!landlord.representative.firstName?.trim() && !landlord.representative.lastName?.trim()) return false;
        if (landlord.representative.firstName?.trim() || landlord.representative.lastName?.trim()) {
          if (!landlord.representative.birthdate) return false;
          if (!landlord.representative.nationalityCountryId) return false;
        }
        if (!landlord.representative.address.street?.trim()) return false;
        if (!landlord.representative.address.zipcode?.trim()) return false;
        if (!landlord.representative.address.city?.trim()) return false;
        if (landlord.representative.isPoliticalyExposed === null) return false;
        if (landlord.representative.isCloseToPoliticalyExposed === null) return false;
        if (!landlord.representative.identificationType) return false;
        if (!landlord.representative.professionId) return false;
        if (!landlord.representative.industrySectorId) return false;
      }
      return true;
    } else {
      if (landlord.matchedPhysicalPersonId) return true;
      if (!landlord.firstName?.trim() && !landlord.lastName?.trim()) return true;
      if (!landlord.birthdate) return false;
      if (!landlord.nationalityCountryId) return false;
      if (!landlord.address.street?.trim()) return false;
      if (!landlord.address.zipcode?.trim()) return false;
      if (!landlord.address.city?.trim()) return false;
      if (landlord.isPoliticalyExposed === null) return false;
      if (landlord.isCloseToPoliticalyExposed === null) return false;
      if (!landlord.identificationType) return false;
      if (!landlord.professionId) return false;
      if (!landlord.industrySectorId) return false;
      return true;
    }
  });

  const propertiesValid = data.properties.every(property => {
    if (property.matchedPropertyId) return true;
    if (!property.address.street?.trim()) return false;
    if (!property.address.zipcode?.trim()) return false;
    if (!property.address.city?.trim()) return false;
    if (property.surfaceArea != null && property.surfaceArea < 0) return false;
    return true;
  });

  return landlordsValid && propertiesValid;
});

const onIdentityFileAdd = (event, landlordIndex) => {
  const file = event.target.files[0];
  if (!file) return;
  event.target.value = "";

  const landlord = data.landlords[landlordIndex];
  if (!landlord || landlord.identityFiles.length >= 2) return;

  landlord.identityFiles.push(file);
  landlord.identityExtracted = false;
  identityError.value = null;
  identityErrorIndex.value = null;
};

const removeIdentityFile = (landlordIndex, fileIndex) => {
  const landlord = data.landlords[landlordIndex];
  if (!landlord) return;
  landlord.identityFiles.splice(fileIndex, 1);
  landlord.identityExtracted = false;
};

const onExtractIdentity = (landlordIndex) => {
  const landlord = data.landlords[landlordIndex];
  if (!landlord || landlord.identityFiles.length === 0) return;

  identityError.value = null;
  identityErrorIndex.value = null;
  extractingIdentityIndex.value = landlordIndex;

  emit("extract-identity", { files: landlord.identityFiles.map(f => toRaw(f)), sellerIndex: landlordIndex });
};

const mergeIdentityData = (landlordIndex, identityData) => {
  extractingIdentityIndex.value = null;
  const landlord = data.landlords[landlordIndex];
  if (!landlord) return;

  landlord.identityExtracted = true;

  const target = landlord.type === "company" && landlord.representative ? landlord.representative : landlord;

  if (identityData.lastName) target.lastName = identityData.lastName;
  if (identityData.firstName) target.firstName = identityData.firstName;
  if (identityData.birthdate) target.birthdate = identityData.birthdate;
  if (identityData.birthCity) target.birthCity = identityData.birthCity;
  if (identityData.nationalNumber) target.nationalNumber = identityData.nationalNumber;
  if (identityData.nationalityCountryId) target.nationalityCountryId = identityData.nationalityCountryId;
  if (identityData.address?.street) target.address.street = identityData.address.street;
  if (identityData.address?.city) target.address.city = identityData.address.city;
  if (identityData.address?.zipcode) target.address.zipcode = identityData.address.zipcode;

  if (identityData.matchedPhysicalPersonId) {
    target.matchedPhysicalPersonId = identityData.matchedPhysicalPersonId;
    target.matchedPhysicalPersonName = identityData.matchedPhysicalPersonName;
    target.matchConfidence = identityData.matchConfidence;
  }
};

const onIdentityError = (landlordIndex, message) => {
  extractingIdentityIndex.value = null;
  identityError.value = message;
  identityErrorIndex.value = landlordIndex;
};

const getReviewedData = () => {
  return {
    fileId: data.fileId,
    endDate: data.endDate,
    landlords: data.landlords,
    properties: data.properties
  };
};

const getIdentityFiles = () => {
  return data.landlords.flatMap(l => (l.identityFiles || []).map(f => toRaw(f)));
};

const getIdentityFilesPerSeller = () => {
  return data.landlords.map((l, idx) => ({
    sellerIndex: idx,
    sellerType: l.type,
    files: (l.identityFiles || []).map(f => toRaw(f))
  }));
};

defineExpose({ getReviewedData, mergeIdentityData, onIdentityError, isValid, getIdentityFiles, getIdentityFilesPerSeller });
</script>
